import { EventBus } from '@/event-bus'
import { Artifact, ArtifactType, Link, LinkType, SnaphotReference } from '@/models'
import { IArtifactService } from '@/services/IArtifactService'
import { defineStore } from 'pinia'
import { useBaseStore } from '.'
import { ISearchService } from '@/services/ISearchService'
import _ from 'underscore'

interface TraceApiRequest {
  artifactService: IArtifactService
  projectId: number
  snapshotId: SnaphotReference
  id: number
  direction: 'incoming' | 'outgoing' | 'any'
  filterId?: number
  excludeArtifacts?: boolean
}

export interface ArtifactCollectionRequest {
  artifactService: IArtifactService
  projectId: number
  snapshotId: SnaphotReference
  typeId: number
  filterId?: number
  include?: string
  sortBy?: string
  page?: number
  pageSize?: number
}

interface SearchApiRequest {
  searchService: ISearchService
  searchString: string
  snapshotId: SnaphotReference
  projectId: number
  page: number
  filterId?: number
  itemsPerPage?: number
}

interface ModelState {
  artifacts: Artifact[]
  links: Link[]
  artifactTypes: ArtifactType[]
  linkTypes?: LinkType[]
}

interface TraceDataState {
  loadedArtifacts?: Artifact[]
  modelState: ModelState
  snapshotId: string | undefined
  artifactId: string | undefined
  highlightAllDirectLinksOnSelection: boolean
  searchMode: boolean
}

export const useTraceDataStore = defineStore('traceDataStore', {
  state: (): TraceDataState => ({
    modelState: {} as ModelState,
    snapshotId: undefined,
    artifactId: undefined,
    highlightAllDirectLinksOnSelection: false,
    searchMode: false,
  }),
  actions: {
    async searchArtifactsForArtifactType(
      artifactType: ArtifactType,
      searchParams: SearchApiRequest,
    ) {
      const baseStore = useBaseStore()
      baseStore.startNewJob()
      let searchString = searchParams.searchString
      if (
        !searchString.startsWith('+') &&
        !searchString.startsWith('-') &&
        !searchString.startsWith('(')
      ) {
        // By default assume that the string has to match.
        searchString = '+' + searchString
      }

      const maskedArtifactType = artifactType.name.replaceAll(
        /([-+=&|><!(){}[\]^"~*?:\\/])/g,
        '\\\\$1',
      )
      try {
        const response = await searchParams.searchService.searchSnapshotArtifacts(
          `+(artifactTypeName:"${maskedArtifactType}") ${searchString}`,
          searchParams.projectId,
          searchParams.snapshotId,
          searchParams.page,
          searchParams.filterId,
          searchParams.itemsPerPage,
        )
        this.setArtifactsForArtifactType(artifactType, response.data)
        artifactType.containerSearchArtifactCount = response.pagination?.totalElements
      } catch (error) {
        console.error('Error in searchArtifactsForArtifactType:', error)
      } finally {
        baseStore.decrementRunningJobs()
      }
    },
    setArtifactsForArtifactType(artifactType: ArtifactType, artifacts: Artifact[]) {
      // Keep all artifacts from different artifact types
      this.modelState.artifacts = this.modelState.artifacts.filter(
        (a) => a.artifactType.id !== artifactType.id,
      )

      this.modelState.artifacts = this.modelState.artifacts.concat(artifacts)
    },
    async loadTraceDataOverviewFromApi(requestParams: TraceApiRequest) {
      const baseStore = useBaseStore()
      baseStore.startNewJob()
      await requestParams.artifactService
        .getArtifactTypes(requestParams.projectId, requestParams.snapshotId, requestParams.filterId)
        .then((response) => {
          this.setArtifactTypes(response)
        })
        .catch(() => {
          // nothing yet
          baseStore.decrementRunningJobs()
        })
      await requestParams.artifactService
        .getLinkTypes(requestParams.projectId, requestParams.snapshotId, requestParams.filterId)
        .then((response) => {
          this.setLinkTypes(response)
        })
        .catch(() => {
          // nothing yet
          baseStore.decrementRunningJobs()
        })
      baseStore.decrementRunningJobs()
    },
    async loadTraceDataFromApi(requestParams: TraceApiRequest) {
      const baseStore = useBaseStore()
      baseStore.startNewJob()
      await requestParams.artifactService
        .getLinkedArtifacts(
          requestParams.projectId,
          requestParams.snapshotId,
          requestParams.id,
          'any',
          requestParams.filterId,
        )
        .then((response) => {
          //this.setModelArtifacts(response.artifacts)
          if (!this.modelState.artifacts) {
            this.setModelArtifacts(response.artifacts)
          } else {
            this.updateModelArtifacts(response.artifacts)
          }
          this.setModelLinks(response.links)
          this.getModelArtifactTypesFromArtifacts()
          this.setSnapshotId(requestParams.snapshotId.toString())
          baseStore.decrementRunningJobs()

          //EventBus.emit('traceModelStateChanged')
        })
        .catch(() => {
          // nothing yet
          baseStore.decrementRunningJobs()
        })
    },
    async addTraceDataToOverview(requestParams: TraceApiRequest) {
      const baseStore = useBaseStore()
      baseStore.startNewJob()
      await requestParams.artifactService
        .getLinkedArtifacts(
          requestParams.projectId,
          requestParams.snapshotId,
          requestParams.id,
          'any',
          requestParams.filterId,
        )
        .then((response) => {
          this.setModelArtifacts(response.artifacts)
          this.updateModelLinks(response.links)
          this.expandArtifactTypesForLoadedArtifacts(response.artifacts)
          this.setSnapshotId(requestParams.snapshotId.toString())
          baseStore.decrementRunningJobs()

          //EventBus.emit('traceModelStateChanged')
        })
        .catch(() => {
          // nothing yet
          baseStore.decrementRunningJobs()
        })
    },
    async loadArtifactsCollectionFromApi(requestParams: ArtifactCollectionRequest) {
      const baseStore = useBaseStore()
      baseStore.startNewJob()
      await requestParams.artifactService
        .getArtifactsCollection(
          requestParams.projectId,
          requestParams.snapshotId,
          requestParams.typeId,
          requestParams.filterId,
          requestParams.include,
          requestParams.sortBy,
          requestParams.page,
          requestParams.pageSize,
        )
        .then((response) => {
          if (!this.modelState.artifacts) {
            this.setModelArtifacts(response)
          } else {
            this.updateModelArtifacts(response)
          }
          this.setModelLinks([])
        })
        .catch(() => {
          baseStore.decrementRunningJobs()
        })
      baseStore.decrementRunningJobs()
    },
    async loadTraceDataForArtifactFromApi(requestParams: TraceApiRequest) {
      const baseStore = useBaseStore()
      baseStore.startNewJob()

      await requestParams.artifactService
        .getArtifact(
          requestParams.projectId,
          requestParams.snapshotId,
          requestParams.id,
          requestParams.filterId,
        )
        .then((response) => {
          this.initArtifactTypes()
          this.setModelArtifacts([response])
          this.setModelLinks([])
          this.getModelArtifactTypesFromArtifacts()
          this.setSnapshotId(requestParams.snapshotId.toString())
          baseStore.decrementRunningJobs()

          EventBus.emit('traceModelStateChanged')
        })
        .catch(() => {
          baseStore.decrementRunningJobs()
        })
    },
    async setTraceDataFromApi(requestParams: TraceApiRequest) {
      const baseStore = useBaseStore()
      baseStore.startNewJob()
      await requestParams.artifactService
        .getLinkedArtifacts(
          requestParams.projectId,
          requestParams.snapshotId,
          requestParams.id,
          'any',
          requestParams.filterId,
        )
        .then((response) => {
          this.setModelLinks(response.links)
          this.getModelArtifactTypesFromArtifacts()
          this.setSnapshotId(requestParams.snapshotId.toString())
          baseStore.decrementRunningJobs()
        })
        .catch((e) => {
          // nothing yet
          console.error(e)
          baseStore.decrementRunningJobs()
        })
    },
    async updateTraceDataFromApi(requestParams: TraceApiRequest) {
      const baseStore = useBaseStore()
      baseStore.startNewJob()
      await requestParams.artifactService
        .getLinkedArtifacts(
          requestParams.projectId,
          requestParams.snapshotId,
          requestParams.id,
          'any',
          requestParams.filterId,
        )
        .then((response) => {
          if (!requestParams.excludeArtifacts) {
            this.updateModelArtifacts(response.artifacts)
          }
          this.updateModelLinks(response.links)
          this.getModelArtifactTypesFromArtifacts()
          this.setSnapshotId(requestParams.snapshotId.toString())
          baseStore.decrementRunningJobs()
        })
        .catch(() => {
          // nothing yet
          baseStore.decrementRunningJobs()
        })
    },
    setModelArtifacts(artifacts: Artifact[]) {
      this.modelState.artifacts = artifacts
    },
    setModelLinks(links: Link[]) {
      this.modelState.links = links
    },
    updateModelArtifacts(artifacts: Artifact[]) {
      artifacts.forEach((newArtifact) => {
        const compare = this.modelState.artifacts.filter((artifact) => {
          return artifact.id === newArtifact.id
        })
        if (compare.length === 0) {
          this.modelState.artifacts.push(newArtifact)
        }
      })
    },
    updateModelLinks(links: Link[]) {
      if (this.modelState.links) {
        links.forEach((newLink) => {
          const compare = this.modelState.links.filter((link) => {
            return link.id === newLink.id
          })
          if (compare.length === 0) {
            this.modelState.links.push(newLink)
          }
        })
      } else {
        this.modelState.links = links
      }
    },
    initArtifactTypes() {
      this.modelState.artifactTypes = [] as ArtifactType[]
    },
    setArtifactTypes(artifactTypes: ArtifactType[]) {
      artifactTypes.forEach((artifactType) => {
        artifactType.collapsed = true
      })
      this.modelState.artifactTypes = artifactTypes
    },
    updateArtifactTypes(artifactTypes: ArtifactType[]) {
      artifactTypes.forEach((at: ArtifactType) => {
        if (!this.modelState.artifactTypes.includes(at)) {
          this.modelState.artifactTypes.push(at)
        }
      })
    },
    getModelArtifactTypesFromArtifacts() {
      if (!this.modelState.artifactTypes) {
        this.initArtifactTypes()
      }
      this.modelState.artifacts.forEach((artifact) => {
        const artifactTypeToPush = artifact.artifactType
        artifactTypeToPush.collapsed = false
        if (this.modelState.artifactTypes.length === 0) {
          this.modelState.artifactTypes.push(artifactTypeToPush)
        } else {
          if (
            this.modelState.artifactTypes.filter((artifactType) => {
              return artifactType.id === artifact.artifactType.id
            }).length === 0
          ) {
            this.modelState.artifactTypes.push(artifactTypeToPush)
          }
        }
      })
    },
    expandArtifactTypesForLoadedArtifacts(loadedArtifacts: Artifact[]) {
      const artifactTypeIds = loadedArtifacts.map((artifact: Artifact) => {
        return artifact.artifactType.id
      })

      const groupLoadedArtifacts = _.groupBy(loadedArtifacts, (la) => la.artifactType.id)
      this.modelState.artifactTypes.forEach((at: ArtifactType) => {
        if (artifactTypeIds.includes(at.id)) {
          at.collapsed = false
          at.maxArtifactCountToShow = groupLoadedArtifacts[at.id].length
        }
      })
    },
    setArtifactId(artifactId: string | undefined) {
      this.artifactId = artifactId
    },
    toggleArtifactTypeCollapsed(artifactType: ArtifactType, collapsed: boolean) {
      this.modelState.artifactTypes
        .filter((artifactTypeInState) => {
          return artifactTypeInState.id === artifactType.id
        })
        .forEach((at) => {
          at.collapsed = collapsed
        })
    },
    getArtifactType(artifactType: Pick<ArtifactType, 'id'>): ArtifactType | undefined {
      return this.modelState.artifactTypes.find((at) => at.id === artifactType.id)
    },
    toggleHighlightAllDirectLinksOnSelection() {
      this.highlightAllDirectLinksOnSelection = !this.highlightAllDirectLinksOnSelection
    },
    setSnapshotId(snapshotId: string | undefined) {
      this.snapshotId = snapshotId
    },
    setMaxArtifactCountToShow(artifactType: ArtifactType, maxArtifactCountToShow: number) {
      this.modelState.artifactTypes
        .filter((artifactTypeInState) => {
          return artifactTypeInState.id === artifactType.id
        })
        .forEach((at) => {
          at.maxArtifactCountToShow = maxArtifactCountToShow
        })
    },
    setLinkTypes(linkTypes: LinkType[]) {
      this.modelState.linkTypes = linkTypes
    },
    updateLinkTypes(linkTypes: LinkType[]) {
      linkTypes.forEach((lt: LinkType) => {
        if (!this.modelState.linkTypes?.map((lt) => lt.id).includes(lt.id))
          this.modelState.linkTypes?.push(lt)
      })
    },
    setLoadedArtifacts(artifacts: Artifact[]) {
      this.loadedArtifacts = artifacts
    },
    resetModelState() {
      this.setModelLinks([])
      this.setModelArtifacts([])

      this.setLinkTypes([])
      this.setArtifactTypes([])
    },
    getArtifactTypeForArtifact(artifact: Artifact): ArtifactType | undefined {
      return this.modelState.artifactTypes.find((a) => artifact.artifactType.id === a.id)
    },
    getArtifactsForArtifactType(artifactType: ArtifactType): Artifact[] {
      return this.modelState.artifacts.filter((a) => a.artifactType.id === artifactType.id)
    },
    getLinksForArtifact(artifact: Artifact): Link[] {
      return this.modelState.links.filter(
        (l) => l.artifactAId === artifact.id || l.artifactBId === artifact.id,
      )
    },
  },
})
